<template>
  <v-dialog ref="dialog" v-model="visible" max-width="1500" scrollable>
    <v-card v-if="lauf" class="vorschau">
      <v-toolbar dense dark :color="lauf.modus === 'PREVIEW' ? 'primary' : 'blue-grey'" style="flex: 0 0 auto">
        <v-btn icon @click="visible = false"><v-icon>mdi-close</v-icon></v-btn>
        <v-toolbar-title>{{ titel }} · Lauf {{ lauf.seq }} · {{ fmtDay(lauf.von) }} – {{ fmtDay(lauf.bis) }}</v-toolbar-title>
        <v-spacer/>
        <v-btn v-if="kannFreigeben" color="success" depressed :loading="publishing" @click="confirm = true">
          <v-icon left>mdi-check-decagram</v-icon> Freigeben und veröffentlichen
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-3" style="height: 80vh">
        <v-alert v-if="lauf.fehlercode === 'REGION_INVALID'" type="error" dense>
          Holiday region is not valid for the tenant. – Die Feiertagsregion des Mandanten ist ungültig; es wurde nichts geprüft oder geschrieben.
        </v-alert>
        <v-alert v-else-if="lauf.status === 'BLOCKED'" type="error" dense outlined>
          Die Vorschau enthält {{ lauf.anzahl_fehler }} Fehler<span v-if="probleme.length"> und {{ probleme.length }} unbesetzte Maschinentage</span>.
          Eine Veröffentlichung ist erst nach deren Behebung möglich.
          <div v-for="(p, i) in probleme.slice(0, 8)" :key="i" class="text-caption">{{ problemText(p) }}</div>
        </v-alert>
        <v-alert v-else-if="lauf.modus === 'PREVIEW'" type="success" dense outlined>
          Vorschau fehlerfrei. Mit der Freigabe werden nur die Soll-Felder (Sollzeit, Arbeitszeit, Pausen, Wertigkeit, Schicht)
          der betroffenen Tage geschrieben; Ist-Zeiten, Buchungen, Salden und Abrechnung bleiben unverändert.
        </v-alert>

        <div class="d-flex flex-wrap align-center mb-2">
          <v-chip small class="mr-2 mb-1" color="success" outlined>Neu: {{ lauf.anzahl_neu }}</v-chip>
          <v-chip small class="mr-2 mb-1" color="primary" outlined>Geändert: {{ lauf.anzahl_geaendert }}</v-chip>
          <v-chip small class="mr-2 mb-1" outlined>Unverändert: {{ lauf.anzahl_unveraendert }}</v-chip>
          <v-chip small class="mr-2 mb-1" color="warning" outlined>Gesperrt: {{ lauf.anzahl_gesperrt }}</v-chip>
          <v-chip small class="mr-2 mb-1" color="error" outlined>Fehler: {{ lauf.anzahl_fehler }}</v-chip>
          <span class="text-caption ml-2">
            Feiertagsregion: <b>{{ lauf.feiertagsregion || '–' }}</b> · Adapter: {{ lauf.feiertags_adapter }}
            <span v-if="lauf.freigegeben_von"> · freigegeben von {{ lauf.freigegeben_von }}</span>
            <span v-if="lauf.nachberechnung !== 'NONE'"> · Neuberechnung: {{ nachberechnungText }}</span>
          </span>
        </div>

        <v-tabs v-model="filter" height="36">
          <v-tab key="changes">Änderungen ({{ count(['INSERT', 'UPDATE']) }})</v-tab>
          <v-tab key="errors">Fehler ({{ count(['ERROR']) }})</v-tab>
          <v-tab key="locked">Gesperrt ({{ count(['LOCKED']) }})</v-tab>
          <v-tab key="holidays">Feiertage ({{ zeilen.filter(z => z.ist_feiertag).length }})</v-tab>
          <v-tab key="all">Alle ({{ zeilen.length }})</v-tab>
        </v-tabs>
        <v-data-table dense :headers="headers" :items="gefiltert" :items-per-page="100" :footer-props="{'items-per-page-options': [50, 100, 500, -1]}"
                      sort-by="datum" no-data-text="Keine Einträge." class="mt-1">
          <template v-slot:item.datum="{ item }">
            {{ fmtDay(item.datum) }}
            <v-chip v-if="item.ist_feiertag" x-small color="red lighten-4" class="ml-1">{{ item.feiertagsbezeichnung }}</v-chip>
          </template>
          <template v-slot:item.mita="{ item }">{{ name(item.mita_bis_id) }}</template>
          <template v-slot:item.quelle_typ="{ item }">{{ sources[item.quelle_typ] || '–' }}</template>
          <template v-slot:item.vorher="{ item }"><span class="text-no-wrap">{{ werte(item.vorher_json) }}</span></template>
          <template v-slot:item.nachher="{ item }"><span class="text-no-wrap font-weight-medium">{{ werte(item.nachher_json) }}</span></template>
          <template v-slot:item.aktion="{ item }">
            <v-chip x-small :color="(actions[item.aktion] || {}).color" dark>{{ (actions[item.aktion] || {}).text || item.aktion }}</v-chip>
            <span v-if="item.fehlercode" class="red--text text-caption ml-1">{{ errorCodes[item.fehlercode] || item.fehlercode }}</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirm" max-width="520">
      <v-card v-if="lauf">
        <v-card-title class="text-h5">Plan freigeben?</v-card-title>
        <v-card-text>
          Es werden <b>{{ lauf.anzahl_neu }}</b> neue und <b>{{ lauf.anzahl_geaendert }}</b> geänderte Soll-Tage in die Zeitkontrolle
          übernommen<span v-if="lauf.anzahl_gesperrt">, {{ lauf.anzahl_gesperrt }} bereits bestätigte Tage werden nicht geändert</span>.
          Anschließend werden die betroffenen Tage in der Zeitkontrolle neu berechnet.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="confirm = false">Abbrechen</v-btn>
          <v-btn color="success" text @click="publish">Freigeben</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<style scoped>
.vorschau .v-toolbar {
  flex: 0 0 auto;
}
</style>

<script>
import raiseStack from './raiseStack'
import {ACTIONS, dpApi, ERROR_CODES, errorText, fmtDay, SOURCES} from '@/shared/dienstplan_api'
import {gruppeName, name, position} from '../store'

const FILTERS = [['INSERT', 'UPDATE'], ['ERROR'], ['LOCKED'], null, null]

export default {
  name: 'VorschauDialog',
  mixins: [raiseStack],
  props: {
    canApprove: {type: Boolean, default: false},
  },
  data: () => ({
    visible: false,
    lauf: null,
    zeilen: [],
    probleme: [],
    frisch: false,
    filter: 0,
    confirm: false,
    publishing: false,
    actions: ACTIONS,
    sources: SOURCES,
    errorCodes: ERROR_CODES,
    headers: [
      {text: 'Datum', value: 'datum'},
      {text: 'Mitarbeiter', value: 'mita', sortable: false},
      {text: 'Quelle', value: 'quelle_typ'},
      {text: 'Bisher (Soll · Arbeitszeit · Pausen · Schicht)', value: 'vorher', sortable: false},
      {text: 'Neu', value: 'nachher', sortable: false},
      {text: 'Aktion', value: 'aktion'},
    ],
  }),
  computed: {
    titel () {
      return {PREVIEW: 'Vorschau', PUBLISH: 'Veröffentlichung', ROLLBACK: 'Rücknahme'}[this.lauf.modus] || this.lauf.modus
    },
    kannFreigeben () {
      return this.canApprove && this.lauf.modus === 'PREVIEW' && this.lauf.status === 'OK' && this.frisch
    },
    gefiltert () {
      if (this.filter === 3) {
        return this.zeilen.filter(z => z.ist_feiertag)
      }
      const f = FILTERS[this.filter]
      return f ? this.zeilen.filter(z => f.includes(z.aktion)) : this.zeilen
    },
    nachberechnungText () {
      return {PENDING: 'läuft', DONE: 'abgeschlossen', ERROR: 'fehlgeschlagen'}[this.lauf.nachberechnung] || this.lauf.nachberechnung
    },
  },
  methods: {
    fmtDay,
    // frisch: the preview was just created (older previews are shown read-only)
    open (lauf, zeilen, probleme, frisch) {
      this.lauf = lauf
      this.zeilen = zeilen || []
      this.probleme = probleme || []
      this.frisch = !!frisch
      this.filter = this.count(['ERROR']) ? 1 : 0
      this.visible = true
    },
    count (actions) {
      return this.zeilen.filter(z => actions.includes(z.aktion)).length
    },
    name,
    problemText (p) {
      return fmtDay(p.datum) + ': ' + gruppeName(p.planungsgruppe_id) + ' – ' + ((position(p.fahrerposition_id) || {}).bezeichnung || 'Fahrerposition') + ' unbesetzt'
    },
    werte (json) {
      if (!json) return '–'
      try {
        const m = JSON.parse(json)
        const pausen = [m.pause_1, m.pause_2].filter(p => p && p !== '00:00-00:00').join(', ') || 'keine Pause'
        const soll = Number(m.zeit_soll || 0).toFixed(2).replace('.', ',')
        return soll + ' h · ' + m.soll_zeit + ' · ' + pausen + (m.schicht ? ' · ' + m.schicht : '')
      } catch (e) {
        return json
      }
    },
    publish () {
      this.confirm = false
      this.publishing = true
      dpApi.veroeffentlichen(this.lauf.id).then(res => {
        this.publishing = false
        this.visible = false
        this.$emit('published', res.lauf)
      }, res => {
        this.publishing = false
        this.$emit('error', res, errorText(res))
      })
    },
  },
}
</script>
